import ApiService from '@/core/services/api.service';
import { cmdUrl } from '@/utils/apiUrl';

/**
 * DebtRepository - Handles all debt-related API operations
 * Following the established repository pattern for consistent API management
 */
export default {
  /**
   * Get provider debts with filtering and pagination
   * @param {Object} params - Query parameters for filtering
   * @param {number} params.page - Page number for pagination
   * @param {number} params.pageSize - Number of items per page
   * @param {string} params.fromDate - Start date filter (DD/MM/YYYY format)
   * @param {string} params.toDate - End date filter (DD/MM/YYYY format)
   * @param {number} params.providerId - Provider ID filter
   * @param {string} params.type - Transaction type filter
   * @returns {Promise<Object>} API response with debt data
   */
  async getProviderDebts(params = {}) {
    try {
      const response = await ApiService.query(cmdUrl.DebtUrl.debtProviders, {
        params: { ...params },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching provider debts:', error);
      throw error;
    }
  },

  /**
   * Get installment debts
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response with installment debt data
   */
  async getInstallmentDebts(params = {}) {
    try {
      const response = await ApiService.query(cmdUrl.DebtUrl.debtInstallments, {
        params: { ...params },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching installment debts:', error);
      throw error;
    }
  },

  /**
   * Get credit transactions
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response with credit transaction data
   */
  async getCreditTransactions(params = {}) {
    try {
      const response = await ApiService.query(cmdUrl.DebtUrl.credits, {
        params: { ...params },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching credit transactions:', error);
      throw error;
    }
  },

  /**
   * Get provider transactions
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response with provider transaction data
   */
  async getProviderTransactions(params = {}) {
    try {
      const response = await ApiService.query(cmdUrl.DebtUrl.providers, {
        params: { ...params },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching provider transactions:', error);
      throw error;
    }
  },

  /**
   * Get installment bill transactions
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number for pagination
   * @param {number} params.pageSize - Number of items per page
   * @param {string} params.fromDate - Start date filter (DD/MM/YYYY format)
   * @param {string} params.toDate - End date filter (DD/MM/YYYY format)
   * @param {number} params.storeId - Store ID filter
   * @param {number} params.contactId - Contact/Installment ID filter
   * @param {string} params.contractCode - Contract code filter
   * @param {string} params.docId - Document ID filter
   * @param {string} params.customerMobile - Customer mobile filter
   * @param {string} params.isFullPaid - Payment status filter
   * @returns {Promise<Object>} API response with installment bill transaction data
   */
  async getInstallmentBillTransactions(params = {}) {
    try {
      const response = await ApiService.query(cmdUrl.DebtUrl.installments, {
        params: { ...params },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching installment bill transactions:', error);
      throw error;
    }
  },

  async getInstallmentWarrantyTransactions(params = {}) {
    try {
      const response = await ApiService.query(
        cmdUrl.DebtUrl.installmentWarrantyTransactions,
        { params: { ...params } },
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching installment warranty transactions:', error);
      throw error;
    }
  },

  /**
   * Get installment order transactions
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} API response with installment order transaction data
   */
  async getInstallmentOrderTransactions(params = {}) {
    try {
      const response = await ApiService.query(
        cmdUrl.DebtUrl.installmentOrders,
        {
          params: { ...params },
        },
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching installment order transactions:', error);
      throw error;
    }
  },
};
